import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Avatar, Button, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";

import { AiSparkleIcon } from "./AiSparkleIcon";

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.custom.palette.light,
  fontSize: "1.375rem",
  padding: "8px",
}));

export const StyledHeaderIconButton = styled(StyledIconButton)({
  ["svg"]: {
    fontSize: "1.375rem",
  },
});

export const ChatActionButton = styled(Button)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 700,
  lineHeight: "16px",
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderRadius: theme.custom.radius[1],
  background: theme.custom.palette.semantic.buttonContainedBg,
  border: `1px solid ${theme.custom.palette.semantic.buttonContainedBorder}`,
  color: theme.custom.palette.semantic.buttonContainedColor,
  ["&:hover"]: {
    background: theme.custom.palette.semantic.buttonContainedHoverBg,
    border: `1px solid ${theme.custom.palette.semantic.buttonContainedHoverBorder}`,
    color: theme.custom.palette.semantic.buttonContainedHoverColor,
  },
  ["&:focus"]: {
    background: theme.custom.palette.semantic.buttonContainedFocusBg,
    border: `1px solid ${theme.custom.palette.semantic.buttonContainedFocusBorder}`,
    color: theme.custom.palette.semantic.buttonContainedFocusColor,
  },
  ["& svg"]: {
    fontSize: "1rem",
  },
}));

interface AutomatedMessageProps {
  [key: string]: string;
}
export const AutomatedMessage = styled("p")<AutomatedMessageProps>(({ theme, bottomMargin }) => ({
  fontSize: "0.875rem",
  fontWeight: 400,
  color: theme.palette.text.primary,
  marginTop: "0px",
  marginBottom: bottomMargin && "24px",
  textAlign: "left",
}));

export const PageHeader = styled(Grid)(({ theme }) => ({
  height: "72px",
  width: "100%",
  background: theme.palette.primary.main,
  borderRadius: `${theme.custom.radius[1]} ${theme.custom.radius[1]} 0px 0px`,
  color: theme.palette.text.primary,
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    borderRadius: "0px",
    height: "72px",
  },

  paddingLeft: "24px",
  paddingRight: "24px",
}));

export const HeaderButton = styled(StyledIconButton)(() => ({
  margin: "0px",
}));

export const ChatMessageInputContainer = styled(Grid)(({ theme }) => ({
  width: "100%",
  minHeight: "36px",
  borderRadius: "4px",
  fontFamily: theme.typography.fontFamily,
  fontSize: "14px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
}));

export const SendChatMessageIconButton = styled(IconButton)(({ theme }) => ({
  margin: "0px",
  height: "32px",
  width: "32px",
  flexShrink: 0,
  backgroundColor: theme.custom.palette.semantic.buttonContainedBg,
  borderRadius: "50%",
  border: `1px solid ${theme.custom.palette.semantic.buttonContainedBorder}`,
  color: theme.custom.palette.semantic.buttonContainedColor,
  ["&:hover"]: {
    backgroundColor: theme.custom.palette.semantic.buttonContainedHoverBg,
    border: `1px solid ${theme.custom.palette.semantic.buttonContainedHoverBorder}`,
    color: theme.custom.palette.semantic.buttonContainedHoverColor,
  },
  ["&:disabled"]: {
    backgroundColor: theme.custom.palette.semantic.buttonContainedDisabledBg,
    border: `1px solid ${theme.custom.palette.semantic.buttonContainedDisabledBorder}`,
    color: theme.custom.palette.semantic.buttonContainedDisabledColor,
  },
  ["&:focus"]: {
    backgroundColor: theme.custom.palette.semantic.buttonContainedFocusBg,
    border: `1px solid ${theme.custom.palette.semantic.buttonContainedFocusBorder}`,
    color: theme.custom.palette.semantic.buttonContainedFocusColor,
  },
  ["& svg"]: {
    fontSize: "1rem",
  },
}));

export const ChatMessageInput = styled(TextField)(({ theme }) => ({
  width: "100%",
  minHeight: "36px",
  borderRadius: "4px",
  background: theme.custom.palette.semantic.inputBg,
  fontFamily: theme.typography.fontFamily,
  fontSize: "14px",
  ["& input"]: {
    padding: theme.spacing(1, 1.5),
    fontSize: "14px",
  },
}));

interface BetaBadgeProps {
  mode: "light" | "dark";
}

export const BetaBadge = styled("span")<BetaBadgeProps>(({ theme, mode }) => ({
  fontSize: "10px",
  fontWeight: 600,
  marginLeft: "8px",
  padding: "2px 4px",
  backgroundColor: mode === "light" ? theme.custom.palette.blue[500] : "#ffffff",
  color: mode === "light" ? "#ffffff" : theme.custom.palette.blue[500],
  borderRadius: "4px",
}));

// AI SME Chat Dialog styled components

export const ChatMessagesContainer = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  flexGrow: 1,
  "::-webkit-scrollbar": {
    width: "5px",
  },
  "::-webkit-scrollbar-track": {
    background: theme.custom.palette.semantic.grey,
  },
  "::-webkit-scrollbar-thumb": {
    background: theme.palette.primary.main,
    borderRadius: "10px",
  },
  "::-webkit-scrollbar:window-inactive": {
    visibility: "hidden",
  },
  ["@media screen and (min-height: 765px)"]: {
    minHeight: "622px",
  },
}));

export const MessagesArea = styled("div")(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  minHeight: 0,
  "::-webkit-scrollbar": {
    width: "5px",
  },
  "::-webkit-scrollbar-track": {
    background: theme.custom.palette.semantic.grey,
  },
  "::-webkit-scrollbar-thumb": {
    background: theme.palette.primary.main,
    borderRadius: "10px",
  },
  "::-webkit-scrollbar:window-inactive": {
    visibility: "hidden",
  },
}));

export const ChatInputContainer = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop:
    theme.palette.mode === "dark" ? `1px solid ${theme.palette.grey[700]}` : `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.background.default,
  flexShrink: 0,
}));

export const MessageContainer = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
  "&:last-child": {
    marginBottom: 0,
  },
}));

export const UserMessageContainer = styled(MessageContainer)({
  flexDirection: "row-reverse",
});

export const MessageBubble = styled("div")(({ theme }) => ({
  fontSize: "0.875rem",
  textAlign: "left",
  padding: theme.spacing(1.5),
  borderRadius: theme.custom.radius[1],
  flex: 1,
  minWidth: 0,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[100],
  "& pre, & code": {
    fontSize: "0.8rem",
  },
  "& pre": {
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    borderRadius: "6px",
    border: "1px solid rgba(128,128,128,0.2)",
  },
  "& pre code": {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    whiteSpace: "pre",
    overflowX: "auto",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.42)" : "#ffffff",
  },
  "& pre code::-webkit-scrollbar": {
    height: "5px",
  },
  "& pre code::-webkit-scrollbar-track": {
    background: theme.custom.palette.semantic.grey,
  },
  "& pre code::-webkit-scrollbar-thumb": {
    background: theme.palette.primary.main,
    borderRadius: "10px",
  },
  "& pre code::-webkit-scrollbar:window-inactive": {
    visibility: "hidden",
  },
  "&.user-message": {
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
  },
  "&.ai-message": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[100],
    color: `${theme.palette.text.primary}`,
  },
  "& p:first-of-type": {
    marginTop: 0,
  },
  "& p:last-of-type": {
    marginBottom: 0,
  },
  "& ul li:not(:last-child)": {
    marginBottom: theme.spacing(1),
  },
  "& ol li:not(:last-child)": {
    marginBottom: theme.spacing(1),
  },
}));

export const MessageLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  opacity: 0.7,
}));

export const ChatHeaderContainer = styled("div")({
  flexShrink: 0,
});

export const EmptyStateContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  flex: 1,
  gap: theme.spacing(1),
  minHeight: "100%",
  padding: theme.spacing(4),
}));

export const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 24,
  height: 24,
  backgroundColor: theme.palette.primary.main,
  color: "#ffffff",
  fontSize: "0.875rem",
  flexShrink: 0,
}));

export const AiAvatar = styled(Avatar)(({ theme }) => ({
  width: 24,
  height: 24,
  backgroundColor: theme.palette.grey[500],
  color: "#ffffff",
  fontSize: "0.875rem",
  flexShrink: 0,
}));

export const AiSpinner = styled(CircularProgress)(({ theme }) => ({
  width: 24,
  height: 24,
  flexShrink: 0,
  color: theme.palette.grey[500],
}));

export const StyledAiSparkleIcon = styled(AiSparkleIcon)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? theme.custom.palette.blue[400] : theme.custom.palette.blue[500],
  fontSize: "32px",
}));

export const StyledErrorIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
  color: theme.palette.error[500],
  fontSize: "32px",
}));
